import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStatusStatistics, fetchCustomerStatistics } from '@/redux/actions/laboCaseActions';
import { Card, Table, Row, Col, Spin, DatePicker, Button, Space } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ExperimentOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import { laboCaseStatuses } from '@/utils/helpers';
import PageTitle from '@/components/PageTitle';
const { RangePicker } = DatePicker;

const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/labo-statistics', breadcrumbName: 'Thống kê Labo tổng quan' },
];

const LaboStatistics = () => {
    const dispatch = useDispatch();
    const { statusStatistics: laboStats, customerStatistics: customerStats } = useSelector(state => state.laboCaseReducer);
    const safeLaboStats = Array.isArray(laboStats) ? laboStats : [];
    const safeCustomerStats = Array.isArray(customerStats) ? customerStats : [];

    const [range, setRange] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadAllStatistics = () => {
        const params = {};
        if (Array.isArray(range) && range.length === 2 && range[0] && range[1]) {
            params.send_date_from = range[0].format('YYYY-MM-DD');
            params.send_date_to = range[1].format('YYYY-MM-DD');
        }

        setLoading(true);
        Promise.all([
            dispatch(fetchStatusStatistics(params)),
            dispatch(fetchCustomerStatistics(params))
        ]).finally(() => setLoading(false));
    };

    useEffect(() => {
        loadAllStatistics();
    }, [range]);

    const laboColumns = [
        { title: 'Labo', dataIndex: 'labo_name', key: 'labo_name' },
        ...laboCaseStatuses.map(status => ({
            title: status.label,
            dataIndex: status.value,
            key: status.value,
            align: 'center',
            render: val => <>{val || 0}</>
        }))
    ];

    const customerColumns = [
        { title: 'Khách hàng', dataIndex: 'customer_name', key: 'customer_name' },
        ...laboCaseStatuses.map(status => ({
            title: status.label,
            dataIndex: status.value,
            key: status.value,
            align: 'center',
            render: val => <>{val || 0}</>
        }))
    ];

    const chartData = safeLaboStats.map(item => {
        const row = { labo: item.labo_name };
        laboCaseStatuses.forEach(status => {
            row[status.value] = item[status.value] || 0;
        });
        return row;
    });

    return (
        <>
            <PageTitle title="Thống kê Labo tổng quan" routes={routes} />
            <Spin spinning={loading}>
                <Card
                    title={<><UserOutlined /> Thống kê theo Khách hàng</>}
                    className='mt-3 mb-3'
                    extra={
                        <Space>
                            <RangePicker
                                format="DD/MM/YYYY"
                                onChange={(dates) => setRange(dates || [])}
                                value={range}
                                allowClear
                            />
                            <Button icon={<ReloadOutlined />} onClick={loadAllStatistics}>
                                Làm mới
                            </Button>
                        </Space>
                    }
                >
                    <Table
                        rowKey="customer_id"
                        columns={customerColumns}
                        dataSource={safeCustomerStats}
                        pagination={false}
                        bordered
                        size="small"
                    />
                </Card>
                <Card
                    className="mb-3"
                    title={<><ExperimentOutlined /> Thống kê theo Labo</>}
                >
                    <Row gutter={24}>
                        <Col span={24}>
                            <Table
                                rowKey="labo_id"
                                columns={laboColumns}
                                dataSource={safeLaboStats}
                                pagination={false}
                                bordered
                                size="small"
                                style={{ marginBottom: 24 }}
                            />
                        </Col>
                        <Col span={24}>
                            <Card title="Biểu đồ trạng thái theo Labo">
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="labo" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        {laboCaseStatuses.map(status => (
                                            <Bar
                                                key={status.value}
                                                dataKey={status.value}
                                                stackId="a"
                                                fill={status.color || '#ccc'}
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </Card>
                        </Col>
                    </Row>
                </Card>
            </Spin >
        </>
    );
};

export default LaboStatistics;